import type { LayerSpecification, Map as MapLibreMap } from 'maplibre-gl'
import type { LiveUser } from '@/types/liveview'
import { colorForUser, sourceKind } from '@/lib/utils'

export const CAPTURE_POINTS_SOURCE = 'liveview-capture-points'
export const CAPTURE_CELLS_SOURCE = 'liveview-capture-cells'
export const CAPTURE_ROUTE_SOURCE = 'liveview-capture-route'

export const CAPTURE_CELL_LAYER = 'liveview-capture-cell'
export const CAPTURE_CELL_LINE_LAYER = 'liveview-capture-cell-line'
export const CAPTURE_POINT_LAYER = 'liveview-capture-point'
export const CAPTURE_POINT_NEXT_LAYER = 'liveview-capture-point-next'
export const CAPTURE_ROUTE_LAYER = 'liveview-capture-route-line'

export const USERS_SOURCE = 'liveview-users'
export const TRAILS_SOURCE = 'liveview-trails'
export const ACCURACY_SOURCE = 'liveview-accuracy'
export const JOURNEY_RINGS_SOURCE = 'liveview-journey-rings'

export const USER_DOT_LAYER = 'liveview-user-dot'
export const USER_PULSE_LAYER = 'liveview-user-pulse'
export const USER_LABEL_LAYER = 'liveview-user-label'
export const USER_HEADING_LAYER = 'liveview-user-heading'
export const TRAIL_LAYER = 'liveview-trail'
export const ACCURACY_FILL_LAYER = 'liveview-accuracy-fill'
export const ACCURACY_LINE_LAYER = 'liveview-accuracy-line'
export const JOURNEY_RING_LAYER = 'liveview-journey-ring'

const EARTH_RADIUS_M = 6_378_137

/** A closed ring approximating a circle of `radiusMeters` around a point. */
function circlePolygon(lng: number, lat: number, radiusMeters: number, steps = 48): number[][] {
  const latRad = (lat * Math.PI) / 180
  const dLat = (radiusMeters / EARTH_RADIUS_M) * (180 / Math.PI)
  const dLng = dLat / Math.max(Math.cos(latRad), 1e-6)

  const ring: number[][] = []
  for (let i = 0; i <= steps; i++) {
    const theta = (i / steps) * 2 * Math.PI
    ring.push([lng + dLng * Math.cos(theta), lat + dLat * Math.sin(theta)])
  }
  return ring
}

export interface UserRenderOptions {
  /** Selected floor level; users on other levels are hidden. */
  level: number | null
  /** Show users whose last fix has aged past the server's TTL. */
  showOffline: boolean
  /** Draw the breadcrumb trail. */
  showTrails: boolean
  /** Draw the accuracy circle. */
  showAccuracy: boolean
  /** Phones — anything that posted its own fixes to `/v1/positions`. */
  showPhones: boolean
  /** BlueIOT tags, arriving through the relay consumer. */
  showTags: boolean
  /** Positions LiveView is playing into a relay itself. */
  showJourneys: boolean
  /** Currently selected user, drawn on top and highlighted. */
  selectedUserId: string | null
}

/** A user with no level at all is shown on every floor rather than nowhere. */
function matchesLevel(user: LiveUser, level: number | null): boolean {
  if (level === null || user.level === null || user.level === undefined) return true
  return Math.abs(user.level - level) < 1e-9
}

function matchesKind(user: LiveUser, options: UserRenderOptions): boolean {
  switch (sourceKind(user.source)) {
    case 'tag':
      return options.showTags
    case 'journey':
      return options.showJourneys
    default:
      return options.showPhones
  }
}

export function visibleUsers(users: LiveUser[], options: UserRenderOptions): LiveUser[] {
  return users
    .filter((user) => options.showOffline || user.online)
    .filter((user) => matchesKind(user, options))
    .filter((user) => matchesLevel(user, options.level))
}

export function buildUserFeatures(
  users: LiveUser[],
  options: UserRenderOptions,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: visibleUsers(users, options).map((user) => ({
      type: 'Feature',
      id: undefined,
      geometry: { type: 'Point', coordinates: [user.lng, user.lat] },
      properties: {
        user_id: user.user_id,
        label: user.name ?? user.user_id.slice(0, 12),
        color: colorForUser(user.user_id, user.source),
        kind: sourceKind(user.source),
        online: user.online,
        selected: user.user_id === options.selectedUserId,
        heading: user.heading ?? null,
        hasHeading: typeof user.heading === 'number',
        accuracy: user.accuracy ?? null,
      },
    })),
  }
}

/**
 * Dashed rings around the positions LiveView is playing itself.
 *
 * Its own source rather than a stroke on the dot: a circle layer cannot dash,
 * and a simulated walk that looks exactly like a real device is the one thing
 * this feature must never produce.
 */
export function buildJourneyRingFeatures(
  users: LiveUser[],
  options: UserRenderOptions,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: visibleUsers(users, options)
      .filter((user) => sourceKind(user.source) === 'journey')
      .map((user) => ({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [circlePolygon(user.lng, user.lat, 2.5)] },
        properties: { user_id: user.user_id, color: colorForUser(user.user_id, user.source) },
      })),
  }
}

export function buildAccuracyFeatures(
  users: LiveUser[],
  options: UserRenderOptions,
): GeoJSON.FeatureCollection {
  if (!options.showAccuracy) return { type: 'FeatureCollection', features: [] }
  return {
    type: 'FeatureCollection',
    features: visibleUsers(users, options)
      .filter((user) => typeof user.accuracy === 'number' && user.accuracy > 0)
      .map((user) => ({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [circlePolygon(user.lng, user.lat, user.accuracy as number)],
        },
        properties: {
          user_id: user.user_id,
          color: colorForUser(user.user_id, user.source),
          online: user.online,
        },
      })),
  }
}

export function buildTrailFeatures(
  users: LiveUser[],
  options: UserRenderOptions,
): GeoJSON.FeatureCollection {
  if (!options.showTrails) return { type: 'FeatureCollection', features: [] }
  return {
    type: 'FeatureCollection',
    features: visibleUsers(users, options)
      .map((user) => {
        // A trail crossing floors would draw a line through a wall, so keep only
        // the points recorded on the level being shown.
        const points = user.trail.filter(
          (point) =>
            options.level === null ||
            point.level === null ||
            point.level === undefined ||
            Math.abs(point.level - options.level) < 1e-9,
        )
        return { user, points }
      })
      .filter(({ points }) => points.length >= 2)
      .map(({ user, points }) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates: points.map((point) => [point.lng, point.lat]),
        },
        properties: {
          user_id: user.user_id,
          color: colorForUser(user.user_id, user.source),
          online: user.online,
        },
      })),
  }
}

const layerDefinitions = (): LayerSpecification[] => [
  // --- capture plan, beneath everything live ------------------------------
  {
    id: CAPTURE_CELL_LAYER,
    type: 'fill',
    source: CAPTURE_CELLS_SOURCE,
    paint: {
      // Amber is the gap that still needs walking; green is done. The colour
      // carries the whole message, so it must survive being glanced at.
      'fill-color': ['case', ['get', 'done'], '#10b981', '#f59e0b'],
      'fill-opacity': ['case', ['get', 'done'], 0.22, 0.11],
    },
  },
  {
    id: CAPTURE_CELL_LINE_LAYER,
    type: 'line',
    source: CAPTURE_CELLS_SOURCE,
    // Zoomed out, thousands of 2.5 m cell outlines turn into noise that hides
    // the venue underneath. The fill alone carries coverage at that scale.
    minzoom: 18,
    paint: {
      'line-color': ['case', ['get', 'done'], '#10b981', '#f59e0b'],
      'line-width': ['case', ['get', 'next'], 2.5, 0.6],
      'line-opacity': ['case', ['get', 'next'], 1, 0.3],
    },
  },
  {
    id: CAPTURE_POINT_LAYER,
    type: 'circle',
    source: CAPTURE_POINTS_SOURCE,
    paint: {
      'circle-color': ['case', ['get', 'done'], '#10b981', '#f59e0b'],
      // Partly dwelled points grow towards their finished size, so the operator
      // can see the point filling while standing on it.
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        16,
        ['+', 3, ['*', 2, ['get', 'fill']]],
        21,
        ['+', 7, ['*', 5, ['get', 'fill']]],
      ],
      'circle-opacity': ['case', ['get', 'done'], 0.9, 0.35],
      'circle-stroke-width': 1.5,
      'circle-stroke-color': ['case', ['get', 'done'], '#10b981', '#f59e0b'],
      'circle-stroke-opacity': 0.8,
    },
  },
  {
    id: CAPTURE_POINT_NEXT_LAYER,
    type: 'circle',
    source: CAPTURE_POINTS_SOURCE,
    filter: ['get', 'next'],
    paint: {
      'circle-color': 'rgba(0,0,0,0)',
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 16, 11, 21, 24],
      'circle-stroke-width': 2.5,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-opacity': 0.9,
    },
  },
  {
    id: CAPTURE_ROUTE_LAYER,
    type: 'line',
    source: CAPTURE_ROUTE_SOURCE,
    layout: { 'line-cap': 'round' },
    paint: {
      'line-color': '#ffffff',
      'line-width': 2,
      'line-opacity': 0.7,
      'line-dasharray': [1.5, 1.5],
    },
  },
  // --- live devices --------------------------------------------------------
  {
    id: ACCURACY_FILL_LAYER,
    type: 'fill',
    source: ACCURACY_SOURCE,
    paint: {
      'fill-color': ['get', 'color'],
      'fill-opacity': ['case', ['get', 'online'], 0.12, 0.05],
    },
  },
  {
    id: ACCURACY_LINE_LAYER,
    type: 'line',
    source: ACCURACY_SOURCE,
    paint: {
      'line-color': ['get', 'color'],
      'line-width': 1,
      'line-opacity': ['case', ['get', 'online'], 0.45, 0.2],
    },
  },
  {
    id: TRAIL_LAYER,
    type: 'line',
    source: TRAILS_SOURCE,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ['get', 'color'],
      'line-width': ['interpolate', ['linear'], ['zoom'], 14, 1.5, 20, 4],
      'line-opacity': ['case', ['get', 'online'], 0.55, 0.25],
    },
  },
  {
    // Under the dot, so the dot stays the thing you look at.
    id: JOURNEY_RING_LAYER,
    type: 'line',
    source: JOURNEY_RINGS_SOURCE,
    paint: {
      'line-color': ['get', 'color'],
      'line-width': 1.5,
      'line-opacity': 0.85,
      'line-dasharray': [2, 2],
    },
  },
  {
    id: USER_PULSE_LAYER,
    type: 'circle',
    source: USERS_SOURCE,
    filter: ['get', 'online'],
    paint: {
      'circle-color': ['get', 'color'],
      'circle-radius': 14,
      'circle-opacity': 0.18,
      'circle-blur': 0.35,
    },
  },
  {
    id: USER_HEADING_LAYER,
    type: 'symbol',
    source: USERS_SOURCE,
    filter: ['get', 'hasHeading'],
    layout: {
      'icon-image': 'liveview-heading',
      'icon-rotate': ['get', 'heading'],
      'icon-rotation-alignment': 'map',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'icon-size': ['interpolate', ['linear'], ['zoom'], 15, 0.55, 21, 1],
    },
    paint: { 'icon-opacity': ['case', ['get', 'online'], 0.95, 0.4] },
  },
  {
    id: USER_DOT_LAYER,
    type: 'circle',
    source: USERS_SOURCE,
    paint: {
      'circle-color': ['get', 'color'],
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        14,
        ['case', ['get', 'selected'], 7, 5],
        21,
        ['case', ['get', 'selected'], 13, 9],
      ],
      'circle-opacity': ['case', ['get', 'online'], 1, 0.45],
      'circle-stroke-width': ['case', ['get', 'selected'], 3, 2],
      'circle-stroke-color': ['case', ['get', 'selected'], '#ffffff', 'rgba(255,255,255,0.85)'],
      'circle-stroke-opacity': ['case', ['get', 'online'], 1, 0.5],
    },
  },
  {
    id: USER_LABEL_LAYER,
    type: 'symbol',
    source: USERS_SOURCE,
    minzoom: 16,
    layout: {
      'text-field': ['get', 'label'],
      'text-size': 11,
      'text-offset': [0, 1.4],
      'text-anchor': 'top',
      'text-allow-overlap': false,
      'text-optional': true,
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': 'rgba(0,0,0,0.75)',
      'text-halo-width': 1.4,
      'text-opacity': ['case', ['get', 'online'], 1, 0.5],
    },
  },
]

/** A small white arrow used as the heading indicator. */
function headingIcon(): ImageData | null {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.translate(size / 2, size / 2)
  ctx.beginPath()
  ctx.moveTo(0, -26)
  ctx.lineTo(9, -12)
  ctx.lineTo(0, -16)
  ctx.lineTo(-9, -12)
  ctx.closePath()
  ctx.fillStyle = '#ffffff'
  ctx.strokeStyle = 'rgba(0,0,0,0.35)'
  ctx.lineWidth = 1.5
  ctx.fill()
  ctx.stroke()

  return ctx.getImageData(0, 0, size, size)
}

/**
 * Installs the live layers on top of the loaded Proximi.io style.
 *
 * Called after every `setStyle`, since MapLibre drops non-style sources and
 * layers when the style is replaced.
 */
export function addLiveLayers(map: MapLibreMap): void {
  if (!map.hasImage('liveview-heading')) {
    const icon = headingIcon()
    if (icon) map.addImage('liveview-heading', icon, { pixelRatio: 2 })
  }

  for (const layer of layerDefinitions()) {
    if (map.getLayer(layer.id)) map.removeLayer(layer.id)
    const sourceId = 'source' in layer ? (layer.source as string) : undefined
    if (!sourceId || !map.getSource(sourceId)) continue
    map.addLayer(layer)
  }
}

export const INTERACTIVE_LAYERS = [USER_DOT_LAYER, USER_LABEL_LAYER]

/** Layers that only exist while a capture mode is running. */
export const CAPTURE_LAYERS = [
  CAPTURE_CELL_LAYER,
  CAPTURE_CELL_LINE_LAYER,
  CAPTURE_POINT_LAYER,
  CAPTURE_POINT_NEXT_LAYER,
  CAPTURE_ROUTE_LAYER,
]
